//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BusinessIcon from '@mui/icons-material/Business'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import EmailIcon from '@mui/icons-material/Email'
import SecurityIcon from '@mui/icons-material/Security'
import { Box, Button, Card, CardContent, Grid, IconButton, Paper, TextField, Typography, useTheme } from '@mui/material'
import { useOtDispatch, useOtSelector } from '@react/redux/store'
import React, { ReactElement, useEffect, useState } from 'react'
import EditEmailsModal from './companyProfiles/EditEmailsModal'
import EditWalletsModal from './companyProfiles/EditWalletsModal'
import ManageVaultAccessModal from './companyProfiles/ManageVaultAccessModal'
import VaultAccessItem from './companyProfiles/VaultAccessItem'

import PoolDetailsSkeleton from '@react/components/skeletons/PoolDetailsSkeleton'
import { onPromise } from '@react/components/UiHelpers'
import { companyProfilePut } from '@react/fetchers/companyProfileFetchers'
import { chainActions } from '@react/redux/chainSlice'
import { OtStoreState } from '@react/redux/store'
import { CompanyProfileCTO, VaultAccessStatusEnum } from '@shared/CompanyProfileCTO'
import { logOT } from '@shared/loggingShared'
import { useNavigate, useParams } from 'react-router-dom'

export default function CompanyProfileView(): ReactElement {
  const uuid = useParams().uuid
  const navigate = useNavigate()

  const [companyProfileCTO, setCompanyProfileCTO] = React.useState<CompanyProfileCTO | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditingInstructions, setIsEditingInstructions] = useState(false)
  const [instructionsText, setInstructionsText] = useState('')
  const [isSavingInstructions, setIsSavingInstructions] = useState(false)
  const [isEditingEmails, setIsEditingEmails] = useState(false)
  const [isEditingWallets, setIsEditingWallets] = useState(false)
  const [isManagingVaultAccess, setIsManagingVaultAccess] = useState(false)
  // const [vaultAccessData, setVaultAccessData] = useState<{ poolAddr: string; poolName: string; hasAccess: boolean }[]>(
  //   []
  // )
  // const [isLoadingVaultAccess, setIsLoadingVaultAccess] = useState(false)

  const companyProfiles = useOtSelector((state: OtStoreState) => state.poolState.companyProfiles)

  const {
    cognitoUser,
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtSelector((state: OtStoreState) => state.chainState)
  const dispatch = useOtDispatch()
  const theme = useTheme()

  useEffect(() => {
    if (uuid && companyProfiles.length > 0) {
      setIsLoading(true)
      const profile = companyProfiles.find((profile) => profile.uuid === uuid)
      setCompanyProfileCTO(profile)
      setIsLoading(false)

      // Load vault access data when profile is loaded
      // if (profile && serverApiBaseUrl && restAPIKey) {
      //   loadVaultAccessData(profile)
      // }
    }
  }, [uuid, companyProfiles, serverApiBaseUrl, restAPIKey])

  // const loadVaultAccessData = async (profile: CompanyProfileCTO) => {
  //   if (!serverApiBaseUrl || !restAPIKey) return

  //   setIsLoadingVaultAccess(true)
  //   try {
  //     // Fetch all pool account access pairs
  //     const allAccessPairs = await fetchAllPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey)

  //     // Create vault access info for each pool
  //     const vaultList = Object.entries(poolOverviewCTOs).map(([poolAddr, poolOverview]) => {
  //       // Check if any of the company's wallets have access to this pool
  //       const hasAccess = profile.wallets.some((wallet) =>
  //         allAccessPairs.some(
  //           (pair) =>
  //             pair.accountAddr.toLowerCase() === wallet.toLowerCase() &&
  //             pair.poolAddr.toLowerCase() === poolAddr.toLowerCase()
  //         )
  //       )

  //       return {
  //         poolAddr,
  //         poolName: poolOverview.name,
  //         hasAccess,
  //       }
  //     })

  //     setVaultAccessData(vaultList)
  //   } catch (error) {
  //     logOT.error('Error loading vault access data:', error)
  //   } finally {
  //     setIsLoadingVaultAccess(false)
  //   }
  // }

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  // Instructions handling functions
  const handleEditInstructions = () => {
    setInstructionsText(companyProfileCTO?.instructions || '')
    setIsEditingInstructions(true)
  }

  const handleSaveInstructions = async () => {
    if (!companyProfileCTO || (!serverApiBaseUrl && !(cognitoUser || location.hostname === 'localhost'))) {
      logOT.error('Missing required configuration for saving instructions')
      return
    }

    setIsSavingInstructions(true)
    try {
      const updatedProfile: CompanyProfileCTO = {
        ...companyProfileCTO,
        instructions: instructionsText.trim(),
      }

      const savedProfile = await companyProfilePut(serverApiBaseUrl, updatedProfile, restAPIKey, cognitoUser!)
      dispatch(chainActions.updateCompanyProfile(savedProfile))
      setCompanyProfileCTO(savedProfile)
      setIsEditingInstructions(false)
    } catch (error) {
      logOT.error('Error saving instructions:', error)
    } finally {
      setIsSavingInstructions(false)
    }
  }

  const handleCancelInstructions = () => {
    setIsEditingInstructions(false)
    setInstructionsText('')
  }

  const handleInstructionsKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSaveInstructions()
    }
  }

  const handleEditEmails = () => {
    setIsEditingEmails(true)
  }

  const handleEmailsEditSuccess = (updatedProfile: CompanyProfileCTO) => {
    // Immediately update the local state with the saved profile
    setCompanyProfileCTO(updatedProfile)
  }

  const handleBackToCompanyProfiles = () => {
    navigate('/companyProfiles')
  }

  const handleEditWallets = () => {
    setIsEditingWallets(true)
  }

  const handleWalletsEditSuccess = (updatedProfile: CompanyProfileCTO) => {
    // Immediately update the local state with the saved profile
    setCompanyProfileCTO(updatedProfile)
  }

  const handleManageVaultAccess = () => {
    setIsManagingVaultAccess(true)
  }

  const handleVaultAccessSuccess = (_updatedProfile: CompanyProfileCTO) => {
    // Reload vault access data after changes
    // if (updatedProfile && serverApiBaseUrl && restAPIKey) {
    //   loadVaultAccessData(updatedProfile)
    // }
  }

  // Show loading skeleton while loading or while companyProfiles is empty
  if (isLoading || companyProfiles.length === 0) {
    return <PoolDetailsSkeleton includeTabs={false} />
  }

  // Show not found message only after loading is complete and no profile found
  if (!companyProfileCTO) {
    return (
      <Paper
        data-testid="CompanyProfileView"
        sx={{
          marginTop: '20px',
          marginBottom: 4,
          marginRight: '2rem',
          marginLeft: '2rem',
          padding: '24px',
          borderRadius: 4,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography sx={{ fontWeight: 'bold' }} variant={'h4'}>
          Company Profile Not found
        </Typography>
      </Paper>
    )
  }
  const requestedVaults =
    companyProfileCTO.poolAccessControls?.filter((vault) => vault.status === VaultAccessStatusEnum.REQUESTED) ?? []
  const allowedVaults =
    companyProfileCTO.poolAccessControls?.filter((vault) => vault.status === VaultAccessStatusEnum.ALLOWED) ?? []

  return (
    <Box
      data-testid="CompanyProfileView"
      sx={{
        marginTop: '20px',
        marginBottom: 4,
        marginRight: '2rem',
        marginLeft: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Back Button */}
      <Box display="flex" alignItems="center" mb={2}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToCompanyProfiles}
          sx={{
            color: theme.palette.grey[600],
            '&:hover': {
              backgroundColor: theme.palette.grey[100],
            },
          }}
        >
          Back to Company Profiles
        </Button>
      </Box>
      {/* Overview Section */}
      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[200],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BusinessIcon sx={{ color: theme.palette.grey[600] }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                  {companyProfileCTO.companyName}
                </Typography>
                <Grid item xs={12} key={'uuid2'}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      alignContent={'left'}
                      variant="bodyBold"
                      gutterBottom
                      component="div"
                      sx={{ color: theme.palette.grey[600] }}
                    >
                      {companyProfileCTO.uuid}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(companyProfileCTO.uuid)}
                      sx={{ color: theme.palette.grey[600] }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} key={'originatorUuid'}>
              <Typography alignContent={'left'} variant="bodyBold" gutterBottom component="div">
                Originator UUID: {companyProfileCTO.originatorUuid ?? 'Not Set'}
              </Typography>
            </Grid>
            <Grid item xs={12} key={'referralCreationAccessCode'}>
              <Typography alignContent={'left'} variant="bodyBold" gutterBottom component="div">
                Referral Code: {companyProfileCTO.referralCreationAccessCode ?? 'Not Set'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Emails Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon sx={{ color: theme.palette.grey[600] }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Emails
                  </Typography>
                </Box>
                <Button variant="text" size="small" sx={{ color: theme.palette.grey[600] }} onClick={handleEditEmails}>
                  Manage
                </Button>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                {companyProfileCTO.emails.map((email, index) => (
                  <Typography
                    key={`email-${index}`}
                    variant="body1"
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.palette.grey[50],
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      fontFamily: 'monospace',
                    }}
                  >
                    {email}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Connected Wallets Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AccountBalanceWalletIcon sx={{ color: theme.palette.grey[600] }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Connected Wallets
                  </Typography>
                </Box>
                <Button variant="text" size="small" sx={{ color: theme.palette.grey[600] }} onClick={handleEditWallets}>
                  Manage
                </Button>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                {companyProfileCTO.wallets.map((wallet, index) => (
                  <Box key={`wallet-${index}`} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      sx={{
                        p: 1.5,
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        flex: 1,
                        mr: 1,
                      }}
                    >
                      {wallet}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(wallet)}
                      sx={{ color: theme.palette.grey[600] }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {companyProfileCTO.wallets.length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      py: 2,
                    }}
                  >
                    No wallets connected
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SecurityIcon sx={{ color: theme.palette.grey[600] }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Vault Access
                  </Typography>
                </Box>
                <Button
                  variant="text"
                  size="small"
                  sx={{ color: theme.palette.grey[600] }}
                  onClick={handleManageVaultAccess}
                >
                  Manage
                </Button>
              </Box>
              {requestedVaults.length > 0 && (
                <Box key="RequestedVaults" display="flex" flexDirection="column" gap={1.5}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      py: 2,
                    }}
                  >
                    Vaults Requested access
                  </Typography>
                  {requestedVaults.map((vault) => (
                    <VaultAccessItem
                      key={vault.poolAddr}
                      poolAddr={vault.poolAddr}
                      poolName={vault.displayName || vault.poolName}
                      onCopyAddress={handleCopyToClipboard}
                    />
                  ))}
                </Box>
              )}

              {allowedVaults.length > 0 && (
                <Box key="AllowedVaults" display="flex" flexDirection="column" gap={1.5}>
                  <Typography
                    variant="body2"
                    key="AllowedVaultsTitle"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      py: 2,
                    }}
                  >
                    Vaults with access
                  </Typography>
                  {allowedVaults.map((vault, index) => (
                    <VaultAccessItem
                      key={vault.poolAddr + '-allowed-' + index}
                      poolAddr={vault.poolAddr}
                      poolName={vault.displayName || vault.poolName}
                      onCopyAddress={handleCopyToClipboard}
                    />
                  ))}
                </Box>
              )}

              {allowedVaults.length === 0 && (
                <Typography
                  key="NoAllowedVaults"
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontStyle: 'italic',
                    textAlign: 'center',
                    py: 2,
                  }}
                >
                  No vault access granted
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions Section */}
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Instructions
                  </Typography>
                </Box>
                {!isEditingInstructions && (
                  <IconButton size="small" sx={{ color: theme.palette.grey[600] }} onClick={handleEditInstructions}>
                    <EditIcon />
                  </IconButton>
                )}
              </Box>

              {isEditingInstructions ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={instructionsText}
                    onChange={(e) => setInstructionsText(e.target.value)}
                    onKeyPress={handleInstructionsKeyPress}
                    placeholder="Enter instructions..."
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                      },
                    }}
                  />
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <IconButton
                      size="small"
                      onClick={handleCancelInstructions}
                      disabled={isSavingInstructions}
                      sx={{ color: theme.palette.grey[600] }}
                    >
                      <CloseIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={onPromise(handleSaveInstructions)}
                      disabled={isSavingInstructions}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        '&:disabled': {
                          backgroundColor: theme.palette.grey[400],
                        },
                      }}
                    >
                      <CheckIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box>
                  {companyProfileCTO?.instructions ? (
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.primary,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                      }}
                    >
                      {companyProfileCTO.instructions}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontStyle: 'italic',
                      }}
                    >
                      No instructions provided
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Emails Modal */}
      {companyProfileCTO && (
        <EditEmailsModal
          open={isEditingEmails}
          onClose={() => setIsEditingEmails(false)}
          companyProfile={companyProfileCTO}
          onSuccess={handleEmailsEditSuccess}
        />
      )}

      {/* Edit Wallets Modal */}
      {companyProfileCTO && (
        <EditWalletsModal
          open={isEditingWallets}
          onClose={() => setIsEditingWallets(false)}
          companyProfile={companyProfileCTO}
          onSuccess={handleWalletsEditSuccess}
        />
      )}

      {/* Manage Vault Access Modal */}
      {companyProfileCTO && (
        <ManageVaultAccessModal
          open={isManagingVaultAccess}
          onClose={() => setIsManagingVaultAccess(false)}
          companyProfile={companyProfileCTO}
          onSuccess={handleVaultAccessSuccess}
        />
      )}
    </Box>
  )
}
